import { Button, Card } from '@citizen-walk/shared-ui';
import {
  ASSIGNMENT_PLAN_DISTRICT_KEY,
  type AiVerificationAssignmentPlan,
} from '@citizen-walk/shared-types';
import { Link } from 'react-router-dom';
import type { SessionListItem } from '../sessions-admin';

/**
 * 配分の予約一覧 (2026-09-04)。VerificationAssignmentsPage の上段に置く。
 *
 * 予約ごとに「配り方の要約」と、対象 (セッションごと / 地区全体) 単位の直近実行と実行ボタンを出す。
 *   - セッションごとの予約: 「▶ すべてのセッションを配分」+ 行ごとの「▶ このセッションを配分」
 *   - 地区全体の予約: 「▶ 配分する」
 * 実行の確認・件数表示は RunPlanDialog が担当する。
 */

export interface AssignmentPlanListProps {
  districtId: string;
  plans: AiVerificationAssignmentPlan[];
  templates: { id: string; displayName: string }[];
  sessions: SessionListItem[];
  canManage: boolean;
  onRun: (plan: AiVerificationAssignmentPlan, sessionIds?: string[]) => void;
  onDelete: (plan: AiVerificationAssignmentPlan) => void;
}

const STRATEGY_LABEL: Record<AiVerificationAssignmentPlan['strategy'], string> = {
  random_per_user: 'ランダム配分',
  round_robin: '均等配分',
  all_users: '全員に全投稿',
};

/** 配り方の 1 行要約 (pure、テスト用に export)。 */
export function describePlan(plan: AiVerificationAssignmentPlan): string {
  const parts: string[] = [STRATEGY_LABEL[plan.strategy]];
  const c = plan.constraints;
  if (plan.strategy !== 'all_users' && c.maxPerUser !== undefined) {
    parts.push(`1 人 ${c.maxPerUser} 件まで`);
  }
  if (c.excludeOwnPosts) parts.push('自分の投稿は除外');
  else if ((c.minOwnPosts ?? 0) > 0) {
    parts.push(`自分の投稿 ${c.minOwnPosts} 件以上${c.preferOwnPosts ? '（自分優先）' : ''}`);
  }
  if (plan.scopeMode === 'district' && c.sameSessionOnly) parts.push('参加セッションの投稿だけ');
  if (!plan.candidateFilters.aiAnalyzedOnly) parts.push('AI 解析待ちも含む');
  if (plan.candidateFilters.includeUnlinkedPosts) parts.push('セッション未設定の投稿も含む');
  parts.push(plan.publishOnRun ? '実行と同時に公開' : '下書きで作成');
  if (plan.endAtOffsetDays !== null) parts.push(`期限 ${plan.endAtOffsetDays} 日後`);
  return parts.join(' · ');
}

/** 予約の対象セッション (perSession) を、実行時と同じ規則で解決する: 未選択 = 全アクティブセッション。 */
export function resolvePlanSessions(
  plan: AiVerificationAssignmentPlan,
  sessions: SessionListItem[],
): SessionListItem[] {
  const active = sessions
    .filter((s) => !s.archivedAt)
    .slice()
    .sort(
      (a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER),
    );
  if (plan.targetSessionIds.length === 0) return active;
  return active.filter((s) => plan.targetSessionIds.includes(s.id));
}

function isLocked(plan: AiVerificationAssignmentPlan): boolean {
  if (!plan.runLock) return false;
  const started = Date.parse(plan.runLock.startedAt);
  return Number.isFinite(started) && Date.now() - started < 10 * 60 * 1000;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('ja-JP');
}

export function AssignmentPlanList({
  districtId,
  plans,
  templates,
  sessions,
  canManage,
  onRun,
  onDelete,
}: AssignmentPlanListProps) {
  if (plans.length === 0) {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-600">
          予約はまだありません。イベント前に「予約を作る」で配り方を決めておくと、セッション終了後にボタン
          1 つで配分できます。
        </p>
      </Card>
    );
  }

  return (
    <ul className="grid grid-cols-1 gap-3" data-testid="assignment-plan-list">
      {plans.map((plan) => {
        const tpl = templates.find((t) => t.id === plan.templateId);
        const locked = isLocked(plan);
        const perSession = plan.scopeMode === 'perSession';
        const targets = perSession ? resolvePlanSessions(plan, sessions) : [];
        const districtRun = plan.runs[ASSIGNMENT_PLAN_DISTRICT_KEY];
        const runDisabled = !canManage || locked || plan.status === 'archived';
        return (
          <li key={plan.id}>
            <Card padding="md">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-800">
                      {perSession ? 'セッションごと' : '地区全体'}
                    </span>
                    {locked && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning-100 text-warning-800">
                        実行中…
                      </span>
                    )}
                    <strong className="text-sm text-slate-900 break-all">
                      {plan.displayName || '(無題)'}
                    </strong>
                  </div>
                  <p className="text-xs text-slate-500 break-words">
                    テンプレート: {tpl?.displayName ?? '（見つかりません）'}
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5 break-words">{describePlan(plan)}</p>
                  {plan.description && (
                    <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap break-words">
                      {plan.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  {perSession ? (
                    <Button
                      size="sm"
                      onClick={() => onRun(plan)}
                      disabled={runDisabled || targets.length === 0}
                      aria-label={`${plan.displayName || '(無題)'} のすべてのセッションを配分`}
                    >
                      ▶ すべてのセッションを配分
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => onRun(plan)}
                      disabled={runDisabled}
                      aria-label={`${plan.displayName || '(無題)'} を配分`}
                    >
                      ▶ 配分する
                    </Button>
                  )}
                  <Link
                    to={`/districts/${districtId}/verification/plans/${plan.id}/edit`}
                    className="text-xs text-primary-700 hover:underline"
                  >
                    編集
                  </Link>
                  <button
                    type="button"
                    onClick={() => onDelete(plan)}
                    disabled={!canManage || locked}
                    className="text-xs text-danger-700 hover:underline disabled:opacity-40"
                  >
                    削除
                  </button>
                </div>
              </div>

              {perSession && (
                <div className="mt-3 overflow-x-auto">
                  {targets.length === 0 ? (
                    <p className="text-xs text-warning-800">
                      対象になるセッションがありません（セッションを登録してください）。
                    </p>
                  ) : (
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-slate-200">
                          <th className="py-1 pr-2 font-medium">セッション</th>
                          <th className="py-1 pr-2 font-medium">最後の配分</th>
                          <th className="py-1 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {targets.map((s) => {
                          const run = plan.runs[s.id];
                          return (
                            <tr key={s.id} className="border-b border-slate-100 align-top">
                              <td className="py-1.5 pr-2 font-medium text-slate-900 break-words">
                                {s.name}
                                <span className="block text-[11px] text-slate-500 font-normal">
                                  投稿 {s.postCount} 件
                                </span>
                              </td>
                              <td className="py-1.5 pr-2 text-slate-700">
                                {run ? (
                                  <>
                                    {fmtDate(run.lastRunAt)}（{run.runCount} 回目）
                                    <span className="block text-[11px] text-slate-600">
                                      割当 {run.assignmentCount} 件 / 参加者 {run.userCount} 名
                                      {run.usersWithoutAssignment > 0 &&
                                        ` / 割当なし ${run.usersWithoutAssignment} 名`}
                                      {' · '}
                                      <Link
                                        to={`/districts/${districtId}/verification/assignmentSets/${run.setId}/edit`}
                                        className="text-primary-700 hover:underline"
                                      >
                                        配分セットを見る
                                      </Link>
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-slate-400">未実行</span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => onRun(plan, [s.id])}
                                  disabled={runDisabled}
                                  aria-label={`${s.name} を配分`}
                                >
                                  {run ? '▶ 配分しなおす' : '▶ このセッションを配分'}
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {!perSession && (
                <p className="mt-2 text-xs text-slate-700">
                  {districtRun ? (
                    <>
                      最後の配分: {fmtDate(districtRun.lastRunAt)}（{districtRun.runCount} 回目）·
                      割当 {districtRun.assignmentCount} 件 / 参加者 {districtRun.userCount} 名
                      {districtRun.usersWithoutAssignment > 0 &&
                        ` / 割当なし ${districtRun.usersWithoutAssignment} 名`}
                      {' · '}
                      <Link
                        to={`/districts/${districtId}/verification/assignmentSets/${districtRun.setId}/edit`}
                        className="text-primary-700 hover:underline"
                      >
                        配分セットを見る
                      </Link>
                    </>
                  ) : (
                    <span className="text-slate-400">未実行</span>
                  )}
                </p>
              )}
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
